import React, { useState, useEffect, useRef } from 'react';
import CustomDialog from '../components/CustomDialog';
import AdminNav from './AdminNav';
import './AdminLayout.css';
import './AdminTable.css';

import { API, authHeaders, authHeadersOnly, UPLOADS_URL } from '../services/apiConfig';

function ManageOrders() {
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [dialog, setDialog] = useState(null);
  const [showPdfMenu, setShowPdfMenu] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const pdfMenuRef = useRef(null);

  useEffect(() => { fetchOrders(); }, []);
  useEffect(() => { filterOrders(); }, [statusFilter, orders]);

  useEffect(() => {
    const handler = (e) => { if (pdfMenuRef.current && !pdfMenuRef.current.contains(e.target)) setShowPdfMenu(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const fetchOrders = async () => {
    try {
      const response = await fetch(`${API}/api/orders`, { headers: authHeadersOnly() });
      const data = await response.json();
      const sorted = Array.isArray(data)
        ? [...data].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
        : [];
      setOrders(sorted);
      setFilteredOrders(sorted);
    } catch (error) {
      console.error('Error fetching orders:', error);
      setOrders([]);
    }
  };

  const filterOrders = () => {
    if (statusFilter === 'ALL') setFilteredOrders(orders);
    else setFilteredOrders(orders.filter(o => o.status === statusFilter));
  };

  const acceptOrder = async (id) => { await updateOrderStatus(id, 'PROCESSING'); };

  const updateOrderStatus = async (id, newStatus) => {
    try {
      await fetch(`${API}/api/orders/${id}/status?status=${newStatus}`, { method: 'PUT', headers: authHeadersOnly() });
      setDialog({ message: `Order ${newStatus === 'PROCESSING' ? 'accepted' : 'updated'}`, type: 'success', onClose: () => setDialog(null) });
      fetchOrders();
    } catch (error) {
      setDialog({ message: 'Failed to update status', type: 'error', onClose: () => setDialog(null) });
    }
  };

  const downloadPdf = async (months) => {
    setShowPdfMenu(false);
    setDownloading(true);
    try {
      const res = await fetch(`${API}/api/orders/pdf?months=${months}`, { headers: authHeadersOnly() });
      if (!res.ok) throw new Error('Failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `orders-last-${months}months.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      setDialog({ message: 'PDF download failed. Please try again.', type: 'error', onClose: () => setDialog(null) });
    } finally {
      setDownloading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.href = '/login';
  };

  const btnStyle = { background: '#232f3e', color: 'white', border: 'none', borderRadius: '6px', padding: '8px 16px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' };

  return (
    <>
      <AdminNav onLogout={handleLogout} />
      <div className="admin-page-wrapper">
        <div className="admin-page-container">

          <div className="admin-page-header">
            <h1 className="admin-page-title">Manage Orders</h1>
            <p className="admin-page-subtitle">View, track and manage customer orders</p>
          </div>

          <div className="admin-search-filter-card">
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="admin-filter-select">
              <option value="ALL">All Orders</option>
              <option value="PENDING">Pending</option>
              <option value="PROCESSING">Processing</option>
              <option value="SHIPPED">Shipped</option>
              <option value="DELIVERED">Delivered</option>
              <option value="CANCELLED">Cancelled</option>
            </select>
            <div style={{ marginLeft: 'auto', display: 'flex', gap: '10px', alignItems: 'center' }}>
              <button onClick={fetchOrders} style={btnStyle}>↻ Refresh</button>
              <div style={{ position: 'relative' }} ref={pdfMenuRef}>
                <button onClick={() => setShowPdfMenu(v => !v)} style={{ ...btnStyle, opacity: downloading ? 0.7 : 1 }} disabled={downloading}>
                  {downloading ? '⏳ Downloading...' : '⬇ Download PDF'}
                </button>
                {showPdfMenu && (
                  <div style={{ position: 'absolute', top: 'calc(100% + 6px)', right: 0, background: 'white', border: '1px solid #e0e0e0', borderRadius: '8px', boxShadow: '0 4px 16px rgba(0,0,0,0.12)', zIndex: 200, minWidth: '160px' }}>
                    {[{ label: 'Last 1 Month', months: 1 }, { label: 'Last 3 Months', months: 3 }, { label: 'Last 6 Months', months: 6 }, { label: 'Last 1 Year', months: 12 }].map(opt => (
                      <button key={opt.months} onClick={() => downloadPdf(opt.months)}
                        style={{ display: 'block', width: '100%', padding: '10px 16px', background: 'none', border: 'none', textAlign: 'left', cursor: 'pointer', fontSize: '13px', fontWeight: '500', color: '#333', borderBottom: '1px solid #f0f0f0' }}
                        onMouseOver={e => e.currentTarget.style.background = '#f7f8fa'}
                        onMouseOut={e => e.currentTarget.style.background = 'none'}>
                        {opt.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="admin-table-card">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Order ID</th>
                  <th>Customer</th>
                  <th>Company</th>
                  <th>Products</th>
                  <th>Product ID</th>
                  <th>Address</th>
                  <th>Total</th>
                  <th>Payment</th>
                  <th>Invoice Number</th>
                  <th>Brand Logo</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredOrders.map((order) => {
                  let items = [];
                  try { items = JSON.parse(order.items || '[]'); } catch (e) {}
                  return (
                    <tr key={order.id}>
                      <td style={{ fontSize: '13px', color: '#888' }}>#{order.id}</td>
                      <td>
                        <div style={{ fontWeight: '600', color: '#232f3e' }}>{order.fullName || `User #${order.userId}`}</div>
                        {order.customerEmail && <div style={{ fontSize: '12px', color: '#888' }}>{order.customerEmail}</div>}
                        {order.customerPhone && <div style={{ fontSize: '12px', color: '#888' }}>{order.customerPhone}</div>}
                      </td>
                      <td style={{ fontSize: '13px', color: '#555' }}>
                        {order.companyName ? order.companyName : <span style={{ color: '#bbb' }}>—</span>}
                      </td>
                      <td style={{ maxWidth: '200px' }}>
                        {items.map((it, i) => (
                          <div key={i} style={{ fontSize: '13px', color: '#232f3e', marginBottom: '3px' }}>
                            {it.productName}{it.variantColor ? ` (${it.variantColor})` : ''} ×{it.quantity}
                          </div>
                        ))}
                      </td>
                      <td style={{ maxWidth: '130px' }}>
                        {items.map((it, i) => (
                          <div key={i} style={{ marginBottom: '3px' }}>
                            {it.productCustomId
                              ? <span style={{ fontSize: '12px', fontWeight: '700', color: '#9A1111', background: '#fff5f5', border: '1px solid #fecaca', borderRadius: '5px', padding: '2px 8px', display: 'inline-block', whiteSpace: 'nowrap' }}>{it.productCustomId}</span>
                              : <span style={{ fontSize: '11px', color: '#bbb' }}>—</span>
                            }
                          </div>
                        ))}
                      </td>
                      <td style={{ fontSize: '12px', color: '#555', maxWidth: '160px' }}>{order.shippingAddress || '—'}</td>
                      <td style={{ fontWeight: '700', color: '#28a745', whiteSpace: 'nowrap' }}>
                        ₹{order.totalAmount?.toFixed(2) || '0.00'}
                      </td>
                      <td style={{ fontSize: '13px' }}>{order.paymentMethod || '—'}</td>
                      <td style={{ fontSize: '12px', fontWeight: '600', color: '#9A1111', background: '#fff5f5', padding: '6px 10px', borderRadius: '4px', border: '1px solid #fecaca' }}>{order.invoiceNumber || '—'}</td>
                      <td>
                        {order.brandLogoUrl ? (
                          <a href={`${UPLOADS_URL}${order.brandLogoUrl}`} target="_blank" rel="noreferrer">
                            <img src={`${UPLOADS_URL}${order.brandLogoUrl}`} alt="Brand Logo"
                              style={{ height: '48px', maxWidth: '90px', objectFit: 'contain', borderRadius: '6px', border: '1px solid #e0e0e0', background: '#fafafa', padding: '4px', cursor: 'pointer' }} />
                          </a>
                        ) : (
                          <span style={{ color: '#bbb', fontSize: '12px' }}>No logo</span>
                        )}
                      </td>
                      <td>
                        <span className={`admin-status-badge ${order.status?.toLowerCase()}`}>{order.status}</span>
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                          {order.status === 'PENDING' && (
                            <button onClick={() => acceptOrder(order.id)} className="admin-table-btn accept">Accept</button>
                          )}
                          <select value={order.status} onChange={(e) => updateOrderStatus(order.id, e.target.value)} className="admin-table-select">
                            <option value="PENDING">Pending</option>
                            <option value="PROCESSING">Processing</option>
                            <option value="SHIPPED">Shipped</option>
                            <option value="DELIVERED">Delivered</option>
                            <option value="CANCELLED">Cancelled</option>
                          </select>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {filteredOrders.length === 0 && (
            <div className="admin-empty-state">
              <i className="fas fa-shopping-cart"></i>
              <p>No orders found</p>
            </div>
          )}

        </div>
      </div>
      {dialog && <CustomDialog message={dialog.message} type={dialog.type} onClose={dialog.onClose} />}
    </>
  );
}

export default ManageOrders;
